/* Thurst cart offers. Prices are validated against Shopify, never set in the browser. */
(function (root, factory) {
  const model = factory();
  if (typeof module === 'object' && module.exports) module.exports = model;
  if (!root || !root.document || root.ThurstCartUpsell) return;
  const doc = root.document;
  const state = { cart: null, busy: false, queue: Promise.resolve(), error: '', selected: {}, checkoutPermit: false, ready: false, deadline: 0, timerExpired: false };
  const panels = () => [...doc.querySelectorAll('[data-thurst-upsell]')];
  const config = () => {
    const node = doc.querySelector('[data-thurst-upsell-config]');
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (_) { return null; }
  };
  const route = (path) => (root.Shopify?.routes?.root || '/') + path;
  const refreshTheme = () => doc.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
  function money(cents, currency) {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: currency || 'AUD', maximumFractionDigits: 2 }).format(cents / 100);
  }
  async function api(path, body) {
    const response = await fetch(route(path), {
      method: body === undefined ? 'GET' : 'POST', credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
      ...(body === undefined ? {} : { body: JSON.stringify(body) })
    });
    const data = await response.json();
    if (!response.ok || data.errors) throw new Error(data.description || 'We couldn’t update your order. Please try again.');
    return data;
  }
  function serialize(task) {
    const next = state.queue.then(async () => {
      state.busy = true; render();
      try { return await task(); }
      finally { state.busy = false; render(); }
    });
    state.queue = next.catch(() => {});
    return next;
  }
  function announce(message, error) {
    state.error = error ? message : '';
    panels().forEach(panel => {
      const el = panel.querySelector('[data-tu-message]');
      if (el) { el.textContent = message; el.hidden = !message; el.setAttribute('role', error ? 'alert' : 'status'); }
      if (error) { panel.hidden = false; if (!state.cart) panel.querySelector('[data-tu-card]').hidden = true; }
    });
  }
  function broadcast() {
    try { localStorage.setItem('thurst-cart-updated', String(Date.now())); } catch (_) {}
  }
  // One real acceptance window per cart. Expired windows are never restarted.
  // Browser storage preserves the deadline across drawer opens and page refreshes.
  function timerStorageKey(cart, cfg) {
    const token = String(cart.token || 'current-cart');
    let hash = 2166136261;
    for (let i = 0; i < token.length; i++) hash = Math.imul(hash ^ token.charCodeAt(i), 16777619);
    return `thurst-offer-deadline:v1:${cfg.productId}:${hash >>> 0}`;
  }
  function setupTimer(cart, cfg) {
    const minutes = Math.min(120, Math.max(0, Number(cfg.timerMinutes) || 0));
    state.deadline = 0;
    if (!minutes) { state.timerExpired = false; return; }
    const key = timerStorageKey(cart, cfg);
    const eligible = !!model.chooseOffer(cart, cfg);
    for (const storageName of ['localStorage', 'sessionStorage']) {
      try {
        const storage = root[storageName];
        if (!cart.items.length) { storage.removeItem(key); continue; }
        const saved = Number(storage.getItem(key));
        if (Number.isFinite(saved) && saved > 0) { state.deadline = saved; break; }
        if (eligible) {
          const deadline = Date.now() + minutes * 60000;
          storage.setItem(key, String(deadline));
          if (Number(storage.getItem(key)) === deadline) { state.deadline = deadline; break; }
        }
      } catch (_) { /* No persistent storage: omit the timer rather than resetting it on each visit. */ }
    }
    state.timerExpired = state.deadline > 0 && model.remainingSeconds(state.deadline, Date.now()) === 0;
  }
  function offerExpired() { return state.deadline > 0 && model.remainingSeconds(state.deadline, Date.now()) === 0; }
  function tickTimer() {
    const remaining = model.remainingSeconds(state.deadline, Date.now());
    panels().forEach(panel => {
      const text = panel.querySelector('[data-tu-time]');
      if (text && state.deadline) text.textContent = model.formatCountdown(remaining);
    });
    const expired = offerExpired();
    if (expired !== state.timerExpired) { state.timerExpired = expired; render(); }
  }
  function availableVariants(cfg, type) { return cfg.variants.filter(v => v.type === type && v.available); }
  function render() {
    const cfg = config();
    if (!cfg || !state.cart) return;
    const cart = state.cart;
    const selection = model.chooseOffer(cart, cfg);
    const existing = (cart.items || []).find(model.isUpsell);
    panels().forEach(panel => {
      const card = panel.querySelector('[data-tu-card]');
      const message = panel.querySelector('[data-tu-message]');
      const type = existing ? Number(existing.properties._thurst_upsell) : selection?.type;
      const variants = type ? cfg.variants.filter(v => v.type === type && (v.available || (existing && v.id === existing.variant_id))) : [];
      const supported = cart.currency === cfg.currency;
      // Once the add-on is in the cart, its normal cart line is the confirmation.
      // Hide the complete offer on refreshes and drawer reopens as well.
      panel.hidden = !!existing || !((supported && type && variants.length) || state.error);
      card.hidden = !!existing || !(supported && type && variants.length);
      panel.setAttribute('aria-busy', String(state.busy));
      const expired = offerExpired() && !existing;
      const timer = panel.querySelector('[data-tu-timer]');
      if (timer) timer.hidden = !state.deadline || !!existing;
      panel.querySelector('[data-tu-product-row]').hidden = expired;
      panel.querySelector('[data-tu-body]').hidden = expired;
      panel.toggleAttribute('data-tu-expired', expired);
      if (message && state.error) { message.textContent = state.error; message.hidden = false; message.setAttribute('role', 'alert'); }
      if (card.hidden) return;
      const offer = cfg.offers[String(type)];
      let selected = existing ? variants.find(v => v.id === existing.variant_id) : variants.find(v => v.id === state.selected[type]);
      if (!selected) selected = (type === 90 ? variants.find(v => v.flavour === 'Trio Flavour Bundle') : variants.find(v => v.flavour !== selection?.flavour)) || variants[0];
      if (!existing) state.selected[type] = selected.id;
      panel.dataset.supply = String(type);
      panel.style.setProperty('--tu-flavour', selected.colour);
      panel.querySelector('[data-tu-badge]').textContent = expired ? 'QUICK-ADD CLOSED' : type === 30 ? '50% OFF YOUR EXTRA BAG' : 'EXCLUSIVE CART OFFER';
      panel.querySelector('[data-tu-title]').textContent = expired ? 'This quick-add window has ended' : existing ? 'A little more Thurst. Added.' : type === 30 ? 'Try Another Flavour for Half Price + FREE Shipping!' : 'Get 3 More Bags for Just $33.33 Each';
      panel.querySelector('[data-tu-description]').textContent = type === 30 ? 'Add one extra bag to your order. Choose your flavour below.' : 'Add another 90-day supply — three more bags, in your choice of flavour.';
      panel.querySelector('[data-tu-product]').textContent = type === 30 ? '1 extra bag · 30-day supply' : '3 extra bags · 90-day supply';
      const img = panel.querySelector('[data-tu-image]');
      img.alt = `${selected.flavour} — ${type}-day supply`;
      if (img.getAttribute('src') !== selected.image) img.setAttribute('src', selected.image);
      img.hidden = !selected.image;
      panel.querySelector('[data-tu-flavour]').textContent = selected.flavour;
      panel.querySelector('[data-tu-price]').textContent = money(offer.price, cfg.currency);
      panel.querySelector('[data-tu-compare]').textContent = money(offer.compare, cfg.currency);
      panel.querySelector('[data-tu-saving]').textContent = `Save ${money(offer.compare - offer.price, cfg.currency)}`;
      panel.querySelector('[data-tu-shipping]').hidden = !cfg.freeShippingConfirmed;
      const buttons = panel.querySelector('[data-tu-flavours]');
      // Preserve focus during background cart updates.
      const ids = variants.map(v => `${v.id}:${v.colour}:${v.short}`).join(',');
      if (buttons.dataset.ids !== ids) {
        buttons.replaceChildren(); buttons.dataset.ids = ids;
        variants.forEach(variant => {
          const button = doc.createElement('button');
          button.type = 'button'; button.className = 'tu__choice'; button.dataset.tuVariant = variant.id;
          button.style.setProperty('--choice-colour', variant.colour);
          button.setAttribute('aria-label', variant.flavour);
          const label = doc.createElement('span'); label.textContent = variant.short;
          button.append(label); buttons.append(button);
        });
      }
      buttons.querySelectorAll('button').forEach(button => {
        button.setAttribute('aria-pressed', String(Number(button.dataset.tuVariant) === selected.id));
        button.disabled = state.busy || !!existing;
      });
      const add = panel.querySelector('[data-tu-add]');
      add.hidden = !!existing; add.disabled = state.busy || expired;
      panel.querySelector('[data-tu-add-text]').textContent = state.busy ? 'Updating your order…' : type === 30 ? `Yes, I’ll try another flavour · ${money(offer.price, cfg.currency)}` : `Add 3 more bags · ${money(offer.price, cfg.currency)}`;
      panel.querySelector('[data-tu-added]').hidden = !existing;
      panel.querySelector('[data-tu-remove]').disabled = state.busy;
    });
    tickTimer();
  }
  async function reconcile(cart, cfg) {
    let current = cart;
    for (let pass = 0; pass < 3; pass++) {
      const updates = model.repairs(current, cfg);
      if (!Object.keys(updates).length) return current;
      current = await api('cart/update.js', { updates });
    }
    if (Object.keys(model.repairs(current, cfg)).length) throw new Error('Please refresh your cart before checking out.');
    return current;
  }
  async function sync(options = {}) {
    const cfg = config(); if (!cfg) return;
    let cart = await api('cart.js');
    const repairs = model.repairs(cart, cfg);
    const changed = Object.keys(repairs).length > 0;
    cart = await reconcile(cart, cfg);
    state.cart = cart; state.ready = true; setupTimer(cart, cfg);
    if (options.clearError || (!cart.items.some(model.isUpsell) && model.promotionAlreadyUsed(cart, cfg))) {
      state.error = '';
      panels().forEach(panel => { const message = panel.querySelector('[data-tu-message]'); if (message) message.hidden = true; });
    }
    if (changed) { refreshTheme(); broadcast(); }
    render();
    return cart;
  }
  function discountCodes(cart) { return (cart.discount_codes || []).map(d => d.code).filter(Boolean); }
  async function add(panel) {
    const cfg = config();
    const before = await sync({ clearError: true });
    const chosen = model.chooseOffer(before, cfg);
    if (!chosen || before.items.some(model.isUpsell)) return;
    if (offerExpired()) throw new Error('This quick-add window has ended. You can still check out with your original order.');
    const offer = cfg.offers[String(chosen.type)];
    const variant = availableVariants(cfg, chosen.type).find(v => v.id === state.selected[chosen.type]);
    if (!variant || before.currency !== cfg.currency) throw new Error('This offer is not available for your order.');
    const token = root.crypto?.randomUUID ? root.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    let discountChanged = false;
    try {
      await api('cart/add.js', { items: [{ id: variant.id, quantity: 1, properties: {
        _thurst_upsell: String(chosen.type), _thurst_parent: model.fingerprint(chosen.parent), _thurst_offer_token: token
      } }] });
      let after = await api('cart.js');
      if (offer.code) {
        const codes = [...new Set([...discountCodes(before), offer.code])];
        discountChanged = true;
        after = await api('cart/update.js', { discount: codes.join(',') });
      }
      const line = after.items.find(i => i.properties?._thurst_offer_token === token);
      // Do not display success or allow checkout unless Shopify honours the exact price.
      const originalsUnchanged = model.parentsUnchanged(before, after, cfg);
      const native = cfg.pricingMode === 'native-bxgy';
      const priceAccepted = !!line && (native ? model.nativePricingValid(line, after, cfg) :
        Number(line.final_price) === offer.price && Number(line.final_line_price) === offer.price);
      const cartIncrease = Number.isSafeInteger(before.total_price) && Number.isSafeInteger(after.total_price) ? after.total_price - before.total_price : null;
      const reason = !line ? 'ADD_ON_MISSING' : !originalsUnchanged ? 'ORIGINAL_CHANGED' :
        !priceAccepted ? 'DISCOUNT_NOT_APPLIED' : native && cartIncrease !== offer.price ? 'CART_TOTAL_MISMATCH' :
        !model.validUpsell(line, after, cfg) ? 'ADD_ON_NOT_ELIGIBLE' : '';
      // Store only offer diagnostics, never customer information or cart tokens.
      root.ThurstCartUpsell.lastOfferCheck = {
        reason: reason || 'OK', supply: chosen.type, expectedPrice: offer.price,
        returnedUnitPrice: line ? Number(line.final_price) : null,
        returnedLinePrice: line ? Number(line.final_line_price) : null,
        quantity: line?.quantity || 0, originalsUnchanged,
        hasConfiguredDiscountCode: !!offer.code, nativePromotion: native,
        expectedDiscountTitle: native ? offer.discountTitle : '', cartIncrease
      };
      if (reason) {
        console.warn('Thurst offer verification:', root.ThurstCartUpsell.lastOfferCheck);
        throw new Error(['DISCOUNT_NOT_APPLIED', 'CART_TOTAL_MISMATCH'].includes(reason) ?
          'We couldn’t apply the offer price. No extra bag was kept in your order.' :
          reason === 'ORIGINAL_CHANGED' ? 'Your cart changed while adding this offer. Please try again.' :
          'We couldn’t confirm this add-on. Please refresh your cart and try again.');
      }
      state.cart = await reconcile(after, cfg);
      refreshTheme(); broadcast();
      announce('Added to your order. One-time purchase only.', false);
    } catch (error) {
      // Roll back only this attempt; never delete an unrelated cart item.
      try {
        let current = await api('cart.js');
        const attempted = current.items.filter(i => i.properties?._thurst_offer_token === token);
        if (attempted.length) current = await api('cart/update.js', { updates: Object.fromEntries(attempted.map(i => [i.key, 0])) });
        if (discountChanged) current = await api('cart/update.js', { discount: discountCodes(before).join(',') });
        state.cart = current;
      } catch (_) {
        state.error = 'We couldn’t finish updating your cart. Please refresh it before checking out.';
        refreshTheme(); throw new Error(state.error);
      }
      refreshTheme(); throw error;
    }
  }
  function giftKinds() {
    const kinds = new Map();
    doc.querySelectorAll('.sample_free_item_product[data-sample-variant-id]').forEach(el => {
      kinds.set(Number(el.dataset.sampleVariantId), el.dataset.sampleKind || 'travel');
    });
    return kinds;
  }
  async function changeOriginal(key, quantity) {
    const cfg = config();
    const cart = await api('cart.js');
    const line = cart.items.find(i => i.key === key);
    if (!line) { state.cart = cart; refreshTheme(); return; }
    const updates = { [key]: quantity };
    if (quantity === 0 && !model.isUpsell(line)) {
      cart.items.filter(i => model.isUpsell(i) && i.properties._thurst_parent === model.fingerprint(line)).forEach(i => { updates[i.key] = 0; });
    }
    // Preserve the existing theme's subscription gift quantities, using the same rendered gift IDs.
    const kinds = giftKinds();
    const remaining = cart.items.filter(i => !model.isUpsell(i) && !kinds.has(i.variant_id) && i.selling_plan_allocation);
    const counts = { 30: 0, 90: 0 };
    remaining.forEach(i => {
      const type = model.supply(i);
      if (type) counts[type] += updates[i.key] === undefined ? i.quantity : updates[i.key];
    });
    cart.items.forEach(i => {
      const kind = kinds.get(i.variant_id); if (!kind) return;
      // Only reduce existing gifts here; let the original theme award any newly earned gifts.
      const target = kind === 'bottle' ? counts[90] : ['gymtowel', 'program'].includes(kind) ? counts[30] + counts[90] : 0;
      if (i.quantity > target) updates[i.key] = target;
    });
    let next;
    if (quantity > 0) {
      // change.js preserves Shopify's stock/quantity checks for the original.
      try { next = await api('cart/change.js', { id: key, quantity }); }
      catch (error) { refreshTheme(); throw error; }
      delete updates[key];
      if (Object.keys(updates).length) next = await api('cart/update.js', { updates });
    } else {
      // Remove the parent and its linked add-on together, never as two requests.
      next = await api('cart/update.js', { updates });
    }
    next = await reconcile(next, cfg);
    state.cart = next; state.error = ''; setupTimer(next, cfg); refreshTheme(); broadcast();
  }
  function handleError(error) {
    console.warn('Thurst cart offer:', error.message);
    announce(error.message || 'We couldn’t update your order. Please try again.', true);
    render();
  }
  function run(task) { return serialize(task).catch(handleError); }
  let timer;
  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(() => run(() => sync()), 120);
  }
  async function checkout(resume) {
    if (state.busy) return;
    await serialize(async () => {
      const cart = await sync({ clearError: true });
      if (!cart || !cart.items.length) throw new Error('Your cart is empty. Please add your gummies first.');
      const cfg = config();
      if (cart.items.some(i => model.isUpsell(i) && !model.validUpsell(i, cart, cfg))) throw new Error('Please refresh your cart before checking out.');
    });
    state.checkoutPermit = true;
    try { resume(); } finally { setTimeout(() => { state.checkoutPermit = false; }, 0); }
  }
  function isCheckoutElement(el) {
    if (!el) return false;
    if (el.matches('button[name="checkout"], input[name="checkout"], [data-thurst-checkout]')) return true;
    if (el.matches('a[href]')) {
      try { return /\/checkout(?:\/|$)/.test(new URL(el.href, location.href).pathname); } catch (_) {}
    }
    return false;
  }
  // Window capture runs before the theme's document-level remove and quantity handlers.
  root.addEventListener('click', event => {
    if (!(event.target instanceof Element)) return;
    const choice = event.target.closest('[data-tu-variant]');
    if (choice) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (!state.busy) { state.selected[choice.closest('[data-thurst-upsell]').dataset.supply] = Number(choice.dataset.tuVariant); render(); }
      return;
    }
    const addButton = event.target.closest('[data-tu-add]');
    if (addButton) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (!state.busy) run(() => add(addButton.closest('[data-thurst-upsell]')));
      return;
    }
    const removeOffer = event.target.closest('[data-tu-remove], [data-thurst-upsell-remove]');
    if (removeOffer) {
      event.preventDefault(); event.stopImmediatePropagation();
      if (!state.busy) run(async () => {
        const cart = await api('cart.js');
        const upsell = removeOffer.dataset.thurstUpsellRemove ? cart.items.find(i => i.key === removeOffer.dataset.thurstUpsellRemove) : cart.items.find(model.isUpsell);
        if (upsell) await changeOriginal(upsell.key, 0);
      });
      return;
    }
    const normalRemove = event.target.closest('[data-item-remove]');
    const cfg = config();
    if (normalRemove && cfg && state.cart) {
      const key = normalRemove.dataset.id;
      const item = state.cart.items.find(i => i.key === key);
      if (item && Number(item.product_id) === cfg.productId && !model.isUpsell(item)) {
        event.preventDefault(); event.stopImmediatePropagation();
        if (!state.busy) run(() => changeOriginal(key, 0));
        return;
      }
    }
    const checkoutEl = event.target.closest('button, input[type="submit"], a[href]');
    if (!state.checkoutPermit && isCheckoutElement(checkoutEl) && config()) {
      event.preventDefault(); event.stopImmediatePropagation();
      checkout(() => checkoutEl.isConnected && checkoutEl.click()).catch(handleError);
    }
  }, true);
  root.addEventListener('theme:cart:update', event => {
    const cfg = config();
    if (!cfg || !state.cart) return;
    const key = String(event.detail?.id || '');
    const item = state.cart.items.find(i => i.key === key);
    if (!item || Number(item.product_id) !== cfg.productId) return;
    event.stopImmediatePropagation();
    const quantity = Number(event.detail.quantity);
    if (!Number.isInteger(quantity) || quantity < 0) return;
    if (!state.busy) run(() => changeOriginal(key, model.isUpsell(item) ? Math.min(1, quantity) : quantity));
  }, true);
  root.addEventListener('submit', event => {
    const form = event.target;
    if (state.checkoutPermit || !form.matches('[data-cart-form]') || !config()) return;
    if (event.submitter && event.submitter.name !== 'checkout') return;
    event.preventDefault(); event.stopImmediatePropagation();
    checkout(() => form.requestSubmit(event.submitter || form.querySelector('[name="checkout"]') || undefined)).catch(handleError);
  }, true);
  doc.addEventListener('theme:cart:change', schedule);
  doc.addEventListener('theme:cart-drawer:open', schedule);
  root.addEventListener('pageshow', schedule);
  root.addEventListener('focus', schedule);
  root.addEventListener('storage', event => { if (event.key === 'thurst-cart-updated') schedule(); });
  doc.addEventListener('visibilitychange', () => { if (!doc.hidden) schedule(); });
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', schedule, { once: true }); else schedule();
  if (!customElements.get('thurst-cart-offer')) customElements.define('thurst-cart-offer', class extends HTMLElement { connectedCallback() { schedule(); } });
  root.ThurstCartUpsell = { refresh: schedule };
  root.setInterval(tickTimer, 1000);
})(typeof window === 'undefined' ? null : window, function () {
  'use strict';
  const isUpsell = item => ['30', '90'].includes(String(item.properties?._thurst_upsell));
  function supply(item) {
    const text = [...(item.variant_options || []), ...(item.options_with_values || []).map(o => o.value), item.variant_title || ''].join(' ');
    if (/\b(?:30\s*day|1\s*month)\s*supply\b/i.test(text)) return 30;
    if (/\b(?:90\s*day|3\s*month)\s*supply\b/i.test(text)) return 90;
    return 0;
  }
  function fingerprint(item) {
    const props = Object.entries(item.properties || {}).filter(([, value]) => value !== null && value !== '').sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0);
    return JSON.stringify([Number(item.variant_id), Number(item.selling_plan_allocation?.selling_plan?.id || 0), props]);
  }
  function parents(cart, cfg, type) {
    const allowed = new Set(cfg.variants.filter(v => v.type === type).map(v => Number(v.id)));
    return (cart.items || []).filter(i => Number(i.product_id) === cfg.productId && allowed.has(Number(i.variant_id)) && !isUpsell(i) && i.quantity > 0 && i.final_price > 0 && supply(i) === type);
  }
  // Only the two configured product promotions count as this offer.
  // The live Shopify Ajax response can return type: '' for a native Buy X get Y
  // discount. Keep the exact title, eligible variants, amount and cart-total
  // checks; a blank type alone is not a reason to reject a valid promotion.
  // A gift discount, an unrelated coupon, or a price drop alone is insufficient.
  function discountAmountForLine(item, cfg, type) {
    if (cfg.pricingMode !== 'native-bxgy') return 0;
    const title = cfg.offers[String(type)]?.discountTitle;
    if (!title) return 0;
    return (item.line_level_discount_allocations || []).reduce((total, allocation) => {
      const app = allocation.discount_application;
      const amount = Number(allocation.amount);
      const supportedType = app && (app.type === 'automatic' || app.type === '');
      return supportedType && app.title === title && app.target_type === 'line_item' && Number.isSafeInteger(amount) && amount > 0 ? total + amount : total;
    }, 0);
  }
  function promotionAlreadyUsed(cart, cfg) {
    return (cart.items || []).some(item => [30, 90].some(type => discountAmountForLine(item, cfg, type) > 0));
  }
  function nativePricingValid(item, cart, cfg) {
    if (!item || cfg.pricingMode !== 'native-bxgy' || cart.currency !== cfg.currency) return false;
    const type = Number(item.properties?._thurst_upsell);
    const offer = cfg.offers[String(type)];
    const variant = cfg.variants.find(v => v.id === Number(item.variant_id) && v.type === type);
    if (!offer || !variant || !Number.isSafeInteger(variant.regularPrice) ||
        variant.regularPrice !== offer.price + offer.discountAmount ||
        item.quantity !== 1 || item.selling_plan_allocation || Number(item.product_id) !== cfg.productId ||
        Number(item.final_price) !== Number(item.final_line_price) ||
        Number(item.final_line_price) + discountAmountForLine(item, cfg, type) !== variant.regularPrice) return false;
    let total = 0;
    for (const line of cart.items || []) {
      const amount = discountAmountForLine(line, cfg, type);
      if ([30, 90].some(other => other !== type && discountAmountForLine(line, cfg, other) > 0)) return false;
      if (!amount) continue;
      const eligible = cfg.variants.find(v => v.id === Number(line.variant_id) && v.type === type);
      if (!eligible || Number(line.product_id) !== cfg.productId || line.selling_plan_allocation ||
          (isUpsell(line) && line !== item) ||
          Number(line.final_line_price) + amount !== eligible.regularPrice * line.quantity) return false;
      total += amount;
    }
    return total === offer.discountAmount;
  }
  function chooseOffer(cart, cfg) {
    if (cart.currency !== cfg.currency || (cart.items || []).some(isUpsell) || promotionAlreadyUsed(cart, cfg)) return null;
    for (const type of [90, 30]) {
      const eligible = parents(cart, cfg, type);
      if (eligible.length && cfg.variants.some(v => v.type === type && v.available)) {
        const parent = eligible[0];
        const variant = cfg.variants.find(v => v.id === Number(parent.variant_id));
        return { type, parent, flavour: variant?.flavour || '' };
      }
    }
    return null;
  }
  function validUpsell(item, cart, cfg) {
    if (!isUpsell(item) || cart.currency !== cfg.currency || item.selling_plan_allocation || item.quantity !== 1) return false;
    const type = Number(item.properties._thurst_upsell);
    const offer = cfg.offers[String(type)];
    const variant = cfg.variants.find(v => v.id === Number(item.variant_id) && v.type === type);
    const priceValid = cfg.pricingMode === 'native-bxgy' ? nativePricingValid(item, cart, cfg) :
      !!offer && Number(item.final_price) === offer.price && Number(item.final_line_price) === offer.price;
    return !!variant && Number(item.product_id) === cfg.productId && !!offer && priceValid && parents(cart, cfg, type).some(p => fingerprint(p) === item.properties._thurst_parent);
  }
  function repairs(cart, cfg) {
    const updates = {}; let kept = false;
    for (const item of cart.items || []) {
      if (!isUpsell(item)) continue;
      // A quantity > 1 is removed rather than leaving discounted extra units.
      if (kept || !validUpsell(item, cart, cfg)) updates[item.key] = 0;
      else kept = true;
    }
    return updates;
  }
  function parentsUnchanged(before, after, cfg) {
    return before.items.filter(i => Number(i.product_id) === cfg.productId && !isUpsell(i)).every(old => {
      const current = after.items.find(i => !isUpsell(i) && fingerprint(i) === fingerprint(old));
      const basis = line => Number(line.final_line_price) + [30, 90].reduce((sum, type) => sum + discountAmountForLine(line, cfg, type), 0);
      return current && current.quantity === old.quantity && basis(current) === basis(old);
    });
  }
  function remainingSeconds(deadline, now) { return Math.max(0, Math.ceil((Number(deadline) - Number(now)) / 1000)); }
  function formatCountdown(seconds) { return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`; }
  return { isUpsell, supply, fingerprint, parents, chooseOffer, validUpsell, repairs, parentsUnchanged, discountAmountForLine, promotionAlreadyUsed, nativePricingValid, remainingSeconds, formatCountdown };
});
